import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Button, Input, Progress, Spin, message, Modal, Form } from 'antd'
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  MinusOutlined,
  PlusOutlined,
  ReloadOutlined,
  SyncOutlined
} from '@ant-design/icons'
import { useAppSelector } from '../../app/hooks'
import './index.scss'

declare const __API_SERVER_HOST__: string

type InstanceState =
  | 'loading'
  | 'uninitialized'
  | 'creating'
  | 'created'
  | 'deleted'
  | 'error'
type TaskState = 'creating' | 'ready' | 'failed'

interface ClawInstanceInfo {
  instance_id?: number
  instance_name?: string
  created_at?: string
  openclaw?: {
    gateway_ui_url?: string
    gateway_token?: string
  }
}

interface ChannelParam {
  key: string
  label: string
}

interface ChannelInfo {
  channel_id: string
  name: string
  enabled: boolean
  custom: boolean
  custom_config: string
  params: ChannelParam[]
}

interface AdminChannelInfo {
  id: number
  channel_id: string
  name: string
  enabled: boolean
  custom: boolean
  custom_config: string
}

interface CredField {
  key: string
  label: string
}

const HERO_STATS = [
  { value: '4大IM', label: '微信 / 钉钉 / 企微 / 飞书原生接入' },
  { value: '云上管控', label: '统一下发模板、配额与能力组件' },
  { value: '低成本', label: 'Tokens 优化、缓存与记忆共享降本' }
]

const PLATFORM_POINTS = [
  {
    title: '管理员统一养虾',
    desc: '给员工下发助手模板、模型配额和能力组件，不再依赖个人自行配置环境。'
  },
  {
    title: '企业级权限与成本管控',
    desc: '监控 Token 消耗、限制模型访问范围、沉淀审计链路，让智能体更可控。'
  },
  {
    title: '员工直接在 IM 里开工',
    desc: '通过微信、钉钉、企微、飞书直接派活，降低接入和使用门槛。'
  }
]

const CONSOLE_FEATURES = [
  '支持微信 / 钉钉 / 企微 / 飞书四大通道',
  '管理对话入口与交互面板',
  '查看实例入口与使用状态',
  '统一接入能力组件与知识库'
]

const CLOUD_ADVANTAGES = [
  '弹性算力：按需扩容，避免本地机器卡顿',
  '高可用性：云端容灾，服务不中断',
  '权限隔离：管理员统一控制资源和模型',
  '安全合规：统一鉴权、审计、加密链路',
  '生态完善：快速接入知识库、工具与扩展能力'
]

const CHANNEL_ORDER = [
  'openclaw-weixin',
  'weixin',
  'wechat',
  'dingtalk',
  'dingtalk',
  'ding',
  'qiwei',
  'wecom',
  'feishu',
  'qqbot'
]

async function requestClawPro(path: string, options: Record<string, any> = {}) {
  const response = await fetch(`${__API_SERVER_HOST__}${path}`, {
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(options.method && options.method !== 'GET'
        ? { 'Content-Type': 'application/json' }
        : {})
    },
    ...options
  })

  if (!response.ok) {
    throw new Error(`请求失败：${response.status}`)
  }

  const result = await response.json()
  if (result?.code !== 0 && result?.code !== 200) {
    throw new Error(
      result?.message || result?.msg || result?.error || '请求失败'
    )
  }
  return result?.data || {}
}

function normalizeInstanceState(
  rawState?: string,
  data?: { task_id?: string; instance?: ClawInstanceInfo | null }
): InstanceState {
  const state = String(rawState || '').trim()
  if (
    state === 'uninitialized' ||
    state === 'creating' ||
    state === 'created' ||
    state === 'deleted' ||
    state === 'error'
  ) {
    return state
  }

  if (data?.instance?.openclaw?.gateway_ui_url) {
    return 'created'
  }

  if (data?.task_id) {
    return 'creating'
  }

  return 'uninitialized'
}

function getProgressFromMessage(messageText = '', fallback = 45) {
  const msg = String(messageText || '')
  if (!msg) return fallback
  if (/启动|创建用户|创建实例/.test(msg)) return 22
  if (/状态|运行|拉起|部署/.test(msg)) return 48
  if (/gateway|面板|控制台|后台/i.test(msg)) return 76
  if (/skill|mcp|配置|重启|能力/i.test(msg)) return 88
  if (/就绪|ready|完成/i.test(msg)) return 100
  return fallback
}

function extractGatewayToken(instance?: ClawInstanceInfo | null) {
  const explicitToken = instance?.openclaw?.gateway_token
  if (explicitToken) return explicitToken
  const gatewayUrl = instance?.openclaw?.gateway_ui_url
  if (!gatewayUrl) return ''
  const tokenMatch = gatewayUrl.match(/[?&]token=([^&]+)/)
  return tokenMatch ? decodeURIComponent(tokenMatch[1]) : ''
}

const NativeClawProPage: React.FC = () => {
  const mountedRef = useRef(true)
  const timerRef = useRef<number | null>(null)
  const [instanceState, setInstanceState] = useState<InstanceState>('loading')
  const [taskId, setTaskId] = useState('')
  const [taskMessage, setTaskMessage] = useState('')
  const [instance, setInstance] = useState<ClawInstanceInfo | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [createProgress, setCreateProgress] = useState(0)
  const [lastCreateFailed, setLastCreateFailed] = useState(false)
  const [channelsLoading, setChannelsLoading] = useState(false)
  const [channels, setChannels] = useState<ChannelInfo[]>([])
  const [channelFormValues, setChannelFormValues] = useState<
    Record<string, Record<string, string>>
  >({})
  const [savingChannelId, setSavingChannelId] = useState('')
  const [configuredStatusJSON, setConfiguredStatusJSON] = useState('')

  // 管理员通道管理
  const [adminChannels, setAdminChannels] = useState<AdminChannelInfo[]>([])
  const [adminChannelsLoading, setAdminChannelsLoading] = useState(false)
  const [togglingId, setTogglingId] = useState<number | null>(null)
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [addModalVisible, setAddModalVisible] = useState(false)
  const [addSubmitting, setAddSubmitting] = useState(false)
  const [addForm, setAddForm] = useState({
    channel_id: '',
    name: '',
    server_url: '',
    cred_fields: [{ key: '', label: '' }] as CredField[]
  })

  const channelsFetchedRef = useRef(false)

  const group = useAppSelector((state: any) => state.group)
  const groupName = String(group?.groupName || '')

  const clearTimer = () => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }

  const pollStatus = async (currentTaskId: string) => {
    try {
      const data = await requestClawPro(
        `/api/claw/create/status?task_id=${encodeURIComponent(currentTaskId)}`
      )
      if (!mountedRef.current) return

      const taskState = data.task_state as TaskState
      setTaskId(data.task_id || currentTaskId)
      setTaskMessage(data.message || '')
      setCreateProgress((prev) =>
        Math.max(prev, getProgressFromMessage(data.message, 52))
      )

      if (taskState === 'ready') {
        setInstance(data.result || null)
        setInstanceState('created')
        setCreateProgress(100)
        setLastCreateFailed(false)
        clearTimer()
        return
      }

      if (taskState === 'failed') {
        setInstanceState('error')
        setTaskMessage(data.error || data.message || '实例创建失败')
        setCreateProgress(0)
        setLastCreateFailed(true)
        clearTimer()
        return
      }

      setInstanceState('creating')
      timerRef.current = window.setTimeout(
        () => pollStatus(currentTaskId),
        5000
      )
    } catch (error) {
      if (!mountedRef.current) return
      setInstanceState('error')
      setTaskMessage((error as Error)?.message || '进度查询失败')
      setLastCreateFailed(true)
      clearTimer()
    }
  }

  const queryInstance = async () => {
    setSubmitting(true)
    try {
      const data = await requestClawPro('/api/claw/instance')
      if (!mountedRef.current) return

      const nextState = normalizeInstanceState(data.instance_state, {
        task_id: data.task_id,
        instance: data.instance || null
      })
      setInstanceState(nextState)
      setInstance(data.instance || null)
      setTaskId(data.task_id || '')
      setTaskMessage('')
      setLastCreateFailed(false)

      if (nextState === 'creating' && data.task_id) {
        setTaskMessage('环境正在部署中')
        setCreateProgress(36)
        timerRef.current = window.setTimeout(
          () => pollStatus(data.task_id),
          5000
        )
      } else if (nextState === 'created') {
        setCreateProgress(100)
      } else {
        setCreateProgress(0)
      }
    } catch (error) {
      if (!mountedRef.current) return
      setInstanceState('error')
      setTaskMessage((error as Error)?.message || '状态获取失败')
    } finally {
      if (mountedRef.current) {
        setSubmitting(false)
      }
    }
  }

  useEffect(() => {
    mountedRef.current = true
    queryInstance()
    return () => {
      mountedRef.current = false
      clearTimer()
    }
  }, [])

  const statusTitle = useMemo(() => {
    switch (instanceState) {
      case 'uninitialized':
        return '你的企业级 AI 助手还没开通，现在就创建第一只可管可控的数字员工'
      case 'created':
        return '企业级 AI 助手已就绪'
      case 'creating':
        return '实例正在部署中'
      case 'deleted':
        return '实例已销毁，可重新开通'
      case 'error':
        return '暂时无法获取实例状态'
      default:
        return '让企业真正把 AI 助手养成可管、可控、可复制的数字员工'
    }
  }, [instanceState])

  const statusDesc = useMemo(() => {
    if (instanceState === 'uninitialized') {
      return '系统已经确认你当前还没有创建过实例。现在可以一键创建企业版助手，创建完成后会自动轮询状态，并把对话入口与使用状态展示出来。'
    }
    if (instanceState === 'created') {
      return '实例已经准备就绪。你可以直接进入对话入口开始使用，并通过专属 Token 完成身份校验。'
    }
    if (instanceState === 'creating') {
      return taskMessage || '实例与对话入口正在生成，请稍候。'
    }
    if (instanceState === 'deleted') {
      return '之前的实例已经销毁，现在可以重新创建新的原生环境。'
    }
    if (instanceState === 'error') {
      return (
        taskMessage ||
        '当前环境暂未返回可用状态。页面仍然保留企业版能力说明，你可以稍后再次同步。'
      )
    }
    return '这套企业版方案更强调统一部署、配额控制、权限隔离与 IM 接入，让“养虾”真正进入生产环境。'
  }, [instanceState, taskMessage])

  const primaryButtonText =
    instanceState === 'error' && lastCreateFailed
      ? '重新创建实例'
      : instanceState === 'error'
        ? '重新获取状态'
        : '立即开通企业版'

  const statusChipText = useMemo(() => {
    switch (instanceState) {
      case 'uninitialized':
        return '未创建实例'
      case 'creating':
        return '创建中'
      case 'created':
        return '在线可用'
      case 'deleted':
        return '已销毁'
      case 'error':
        return '状态异常'
      default:
        return '状态同步中'
    }
  }, [instanceState])

  const handleCreate = async () => {
    if (submitting || instanceState === 'creating') return
    setSubmitting(true)
    setCreateProgress(12)
    setLastCreateFailed(false)
    try {
      const data = await requestClawPro('/api/claw/create', {
        method: 'POST',
        body: JSON.stringify({
          instance_name: groupName ? `${groupName} 的 AI 助手` : '我的AI助手'
        })
      })
      if (!mountedRef.current) return
      setTaskId(data.task_id || '')
      setInstanceState('creating')
      setTaskMessage('正在启动实例')
      setCreateProgress(28)
      message.success('已开始创建实例')
      if (data.task_id) {
        timerRef.current = window.setTimeout(
          () => pollStatus(data.task_id),
          5000
        )
      }
    } catch (error) {
      if (!mountedRef.current) return
      setInstanceState('error')
      setTaskMessage((error as Error)?.message || '开通失败')
      setCreateProgress(0)
      setLastCreateFailed(true)
      message.error((error as Error)?.message || '开通失败')
    } finally {
      if (mountedRef.current) {
        setSubmitting(false)
      }
    }
  }

  const openUrl = (url?: string) => {
    if (!url) return
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  const copyText = async (text?: string) => {
    if (!text) return
    try {
      await navigator.clipboard.writeText(text)
      message.success('已复制专属 Token')
    } catch (error) {
      message.error('复制失败，请手动复制')
    }
  }

  const gatewayToken = useMemo(() => extractGatewayToken(instance), [instance])
  const sortedChannels = useMemo(() => {
    const orderMap = new Map(CHANNEL_ORDER.map((id, index) => [id, index]))
    return [...channels]
      .filter(
        (channel) => Array.isArray(channel.params) && channel.params.length > 0
      )
      .sort((a, b) => {
        const orderA = orderMap.get(a.channel_id) ?? 999
        const orderB = orderMap.get(b.channel_id) ?? 999
        if (orderA !== orderB) return orderA - orderB
        return a.name.localeCompare(b.name)
      })
  }, [channels])

  const fetchChannels = async () => {
    setChannelsLoading(true)
    try {
      const data = await requestClawPro('/api/claw/channels')
      const nextChannels = Array.isArray(data.channels) ? data.channels : []
      setChannels(nextChannels)
      setConfiguredStatusJSON(data.configured_status_json || '')
      setChannelFormValues((prev) => {
        const next = { ...prev }
        nextChannels.forEach((channel: ChannelInfo) => {
          if (!next[channel.channel_id]) {
            next[channel.channel_id] = {}
          }
        })
        return next
      })
    } catch (error) {
      message.error((error as Error)?.message || '获取通道配置项失败')
    } finally {
      setChannelsLoading(false)
    }
  }

  useEffect(() => {
    if (instanceState !== 'created') {
      channelsFetchedRef.current = false
      return
    }
    if (channelsFetchedRef.current) return
    channelsFetchedRef.current = true
    fetchChannels()
  }, [instanceState])

  const fetchAdminChannels = async () => {
    setAdminChannelsLoading(true)
    try {
      const data = await requestClawPro('/api/claw/admin/channels')
      setAdminChannels(Array.isArray(data.channels) ? data.channels : [])
    } catch (error) {
      message.error((error as Error)?.message || '获取管理员通道列表失败')
    } finally {
      setAdminChannelsLoading(false)
    }
  }

  const handleToggleAdminChannel = async (ch: AdminChannelInfo) => {
    setTogglingId(ch.id)
    try {
      await requestClawPro('/api/claw/admin/channel/toggle', {
        method: 'POST',
        body: JSON.stringify({ id: String(ch.id) })
      })
      message.success(`${ch.name} 已${ch.enabled ? '禁用' : '启用'}`)
      fetchAdminChannels()
    } catch (error) {
      message.error((error as Error)?.message || '操作失败')
    } finally {
      setTogglingId(null)
    }
  }

  const handleDeleteAdminChannel = async (ch: AdminChannelInfo) => {
    setDeletingId(ch.id)
    try {
      await requestClawPro('/api/claw/admin/channel/delete', {
        method: 'POST',
        body: JSON.stringify({ id: String(ch.id) })
      })
      message.success(`${ch.name} 已删除`)
      fetchAdminChannels()
    } catch (error) {
      message.error((error as Error)?.message || '删除失败')
    } finally {
      setDeletingId(null)
    }
  }

  const handleAddAdminChannel = async () => {
    const { channel_id, name, server_url, cred_fields } = addForm
    if (!channel_id.trim()) {
      message.warning('请填写通道标识')
      return
    }
    if (!name.trim()) {
      message.warning('请填写通道名称')
      return
    }
    if (!server_url.trim()) {
      message.warning('请填写 IM 服务器地址')
      return
    }
    for (const f of cred_fields) {
      if (!f.key.trim() || !f.label.trim()) {
        message.warning('凭证字段的 key 和 label 不能为空')
        return
      }
    }
    setAddSubmitting(true)
    try {
      await requestClawPro('/api/claw/admin/channel/add', {
        method: 'POST',
        body: JSON.stringify({
          channel_id: channel_id.trim(),
          name: name.trim(),
          custom_config: {
            server: { url: server_url.trim() },
            cred_fields: cred_fields.map((f) => ({
              key: f.key.trim(),
              label: f.label.trim()
            }))
          }
        })
      })
      message.success('自定义通道添加成功')
      setAddModalVisible(false)
      setAddForm({
        channel_id: '',
        name: '',
        server_url: '',
        cred_fields: [{ key: '', label: '' }]
      })
      fetchAdminChannels()
    } catch (error) {
      message.error((error as Error)?.message || '添加失败')
    } finally {
      setAddSubmitting(false)
    }
  }

  const handleChannelInputChange = (
    channelId: string,
    key: string,
    value: string
  ) => {
    setChannelFormValues((prev) => ({
      ...prev,
      [channelId]: {
        ...(prev[channelId] || {}),
        [key]: value
      }
    }))
  }

  const handleSaveChannel = async (channel: ChannelInfo) => {
    const currentValues = channelFormValues[channel.channel_id] || {}
    const payload: Record<string, string> = {}

    if (!channel.params || channel.params.length === 0) {
      message.warning(`${channel.name} 暂无可填写的手动配置项`)
      return
    }

    for (const param of channel.params || []) {
      const value = String(currentValues[param.key] || '').trim()
      if (!value) {
        message.warning(`请先填写${param.label}`)
        return
      }
      payload[param.key] = value
    }

    setSavingChannelId(channel.channel_id)
    try {
      const data = await requestClawPro('/api/claw/channel/set', {
        method: 'POST',
        body: JSON.stringify({
          channel: channel.channel_id,
          config: payload
        })
      })
      message.success(data.output || `${channel.name}配置成功`)
      fetchChannels()
    } catch (error) {
      message.error((error as Error)?.message || `${channel.name}配置失败`)
    } finally {
      setSavingChannelId('')
    }
  }

  return (
    <div className="native-clawpro-page">
      <section className="native-clawpro-page__hero">
        <button
          className="native-clawpro-page__back"
          onClick={() => window.history.back()}
        >
          <ArrowLeftOutlined />
          <span>返回</span>
        </button>

        <div className="native-clawpro-page__hero-copy">
          <div className="native-clawpro-page__badge">
            <span className="native-clawpro-page__badge-dot" />
            企业版数字员工方案
          </div>
          <div className="native-clawpro-page__eyebrow">企业级智能体工作台</div>
          <h1>{statusTitle}</h1>
          <p>{statusDesc}</p>

          <div className="native-clawpro-page__stats">
            {HERO_STATS.map((item) => (
              <div className="native-clawpro-page__stat" key={item.label}>
                <strong>{item.value}</strong>
                <span>{item.label}</span>
              </div>
            ))}
          </div>

          <div className="native-clawpro-page__actions">
            {(instanceState === 'uninitialized' ||
              instanceState === 'deleted' ||
              instanceState === 'error') && (
              <Button
                type="primary"
                size="large"
                loading={submitting}
                className="native-clawpro-page__primary-btn"
                onClick={
                  instanceState === 'error' && !lastCreateFailed
                    ? queryInstance
                    : handleCreate
                }
              >
                {primaryButtonText}
                <ArrowRightOutlined />
              </Button>
            )}
            {instanceState === 'creating' && (
              <Button
                size="large"
                className="native-clawpro-page__ghost-btn"
                disabled
              >
                <Spin size="small" /> 部署中
              </Button>
            )}
            {instanceState === 'created' && (
              <>
                <div className="native-clawpro-page__dialog-entry">
                  {gatewayToken && (
                    <div className="native-clawpro-page__dialog-token">
                      <div className="native-clawpro-page__dialog-token-label">
                        进入对话入口时请输入专属 Token
                      </div>
                      <div className="native-clawpro-page__dialog-token-row">
                        <code>{gatewayToken}</code>
                        <button
                          type="button"
                          onClick={() => copyText(gatewayToken)}
                        >
                          复制 Token
                        </button>
                      </div>
                    </div>
                  )}
                  <Button
                    type="primary"
                    size="large"
                    className="native-clawpro-page__primary-btn"
                    onClick={() => openUrl(instance?.openclaw?.gateway_ui_url)}
                  >
                    打开对话入口
                    <ArrowRightOutlined />
                  </Button>
                </div>
              </>
            )}
          </div>

          {instanceState === 'creating' && (
            <div className="native-clawpro-page__creating-banner">
              <div className="native-clawpro-page__creating-title">
                <Spin size="small" />
                <span>实例创建中，请稍候</span>
              </div>
              <div className="native-clawpro-page__creating-desc">
                {taskMessage ||
                  '正在创建实例与对话入口地址，页面会自动轮询最新状态。'}
              </div>
              <div className="native-clawpro-page__creating-progress">
                <Progress
                  percent={createProgress}
                  showInfo
                  strokeColor={{
                    '0%': '#ff8e53',
                    '100%': '#59a8ff'
                  }}
                  trailColor="rgba(255,255,255,0.08)"
                />
              </div>
            </div>
          )}

          {taskId && (
            <div className="native-clawpro-page__task">
              <SyncOutlined spin={instanceState === 'creating'} />
              <span>{taskMessage || `任务 ${taskId}`}</span>
            </div>
          )}

          <div className="native-clawpro-page__guarantee">
            <span>统一部署助手模板</span>
            <span>企业级配额与权限管控</span>
            <span>IM 通道原生接入</span>
          </div>
        </div>

        <div className="native-clawpro-page__hero-visual">
          <div className="native-clawpro-page__preview">
            <div className="native-clawpro-page__preview-bar">
              <span />
              <span />
              <span />
            </div>
            <div className="native-clawpro-page__preview-title">
              企业版工作台
            </div>
            <div className="native-clawpro-page__preview-subtitle">
              专属的企业级数字员工工作台
            </div>
            <div className="native-clawpro-page__preview-grid">
              <div className="native-clawpro-page__preview-card native-clawpro-page__preview-card--primary">
                <strong>
                  {instanceState === 'created'
                    ? 'Ready'
                    : instanceState === 'creating'
                      ? 'Deploying'
                      : 'Create Now'}
                </strong>
                <span>
                  {instanceState === 'created'
                    ? '对话入口已连接'
                    : instanceState === 'creating'
                      ? '实例与对话入口生成中'
                      : '尚未创建实例，点击按钮即可开通'}
                </span>
              </div>
              <div className="native-clawpro-page__preview-card">
                <strong>IM 通道</strong>
                <span>微信 / 钉钉 / 企微 / 飞书</span>
              </div>
              <div className="native-clawpro-page__preview-card">
                <strong>权限控制</strong>
                <span>配额、模型、资源统一控制</span>
              </div>
              <div className="native-clawpro-page__preview-card">
                <strong>能力组件</strong>
                <span>企业知识、工具与扩展能力集中接入</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="native-clawpro-page__platform">
        <div className="native-clawpro-page__section-head">
          <span>为什么它更像企业版</span>
          <h2>不是给个人玩的“小龙虾”，而是给团队统一管控的生产力底座</h2>
        </div>
        <div className="native-clawpro-page__platform-grid">
          {PLATFORM_POINTS.map((item) => (
            <div
              className="native-clawpro-page__platform-item"
              key={item.title}
            >
              <strong>{item.title}</strong>
              <p>{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="native-clawpro-page__info">
        <div className="native-clawpro-page__info-card">
          <div className="native-clawpro-page__panel-label">平台能力</div>
          <ul>
            {CONSOLE_FEATURES.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
        <div className="native-clawpro-page__info-card">
          <div className="native-clawpro-page__panel-label">云上部署价值</div>
          <ul>
            {CLOUD_ADVANTAGES.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="native-clawpro-page__status-panel">
        <div className="native-clawpro-page__section-head">
          <span>当前实例状态</span>
        </div>
        <div className="native-clawpro-page__status-shell">
          <div className="native-clawpro-page__status-main">
            <div className="native-clawpro-page__status-chip">
              {statusChipText}
            </div>
            <h3>
              {instance?.instance_name ||
                (groupName ? `${groupName} 的 AI 助手` : '我的AI助手')}
            </h3>
            <p>{statusDesc}</p>
          </div>
          <div className="native-clawpro-page__status-meta">
            <div className="native-clawpro-page__meta-item">
              <span>任务 ID</span>
              <strong>{taskId || '暂无'}</strong>
            </div>
            <div className="native-clawpro-page__meta-item">
              <span>对话入口</span>
              <strong>
                {instance?.openclaw?.gateway_ui_url ? '可访问' : '未生成'}
              </strong>
            </div>
          </div>
        </div>
      </section>

      <section className="native-clawpro-page__admin-channels">
        <div className="native-clawpro-page__section-head">
          <span>通道管理（管理员）</span>
          <h2>启用 / 禁用通道，或添加自定义 IM 通道</h2>
        </div>
        <div className="native-clawpro-page__channels-shell">
          <div className="native-clawpro-page__channels-tip">
            <strong>全局通道配置</strong>
            <span>
              管理可供用户选择的 IM 通道，支持启用、禁用以及添加自定义通道。
            </span>
          </div>
          <div className="native-clawpro-page__admin-channels-actions">
            <Button
              icon={<ReloadOutlined />}
              loading={adminChannelsLoading}
              onClick={fetchAdminChannels}
            >
              {adminChannels.length === 0 && !adminChannelsLoading
                ? '加载通道列表'
                : '刷新'}
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setAddModalVisible(true)}
            >
              添加自定义通道
            </Button>
          </div>
          {adminChannelsLoading ? (
            <div className="native-clawpro-page__channels-loading">
              <Spin size="large" />
              <span>正在拉取通道列表...</span>
            </div>
          ) : adminChannels.length > 0 ? (
            <div className="native-clawpro-page__channels-grid">
              {adminChannels.map((ch) => (
                <div className="native-clawpro-page__channel-card" key={ch.id}>
                  <div className="native-clawpro-page__channel-head">
                    <div>
                      <strong>{ch.name}</strong>
                      <span>{ch.channel_id}</span>
                    </div>
                    <div className="native-clawpro-page__channel-badges">
                      {ch.custom && (
                        <em className="native-clawpro-page__channel-badge--custom">
                          自定义
                        </em>
                      )}
                      <em
                        className={
                          ch.enabled
                            ? 'native-clawpro-page__channel-badge--on'
                            : 'native-clawpro-page__channel-badge--off'
                        }
                      >
                        {ch.enabled ? '已启用' : '已禁用'}
                      </em>
                    </div>
                  </div>
                  <div className="native-clawpro-page__channel-actions">
                    <Button
                      size="small"
                      loading={togglingId === ch.id}
                      onClick={() => handleToggleAdminChannel(ch)}
                    >
                      {ch.enabled ? '禁用' : '启用'}
                    </Button>
                    {ch.custom && (
                      <Button
                        size="small"
                        danger
                        loading={deletingId === ch.id}
                        onClick={() => handleDeleteAdminChannel(ch)}
                      >
                        删除
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </section>

      <Modal
        title="添加自定义通道"
        open={addModalVisible}
        confirmLoading={addSubmitting}
        onOk={handleAddAdminChannel}
        onCancel={() => {
          setAddModalVisible(false)
          setAddForm({
            channel_id: '',
            name: '',
            server_url: '',
            cred_fields: [{ key: '', label: '' }]
          })
        }}
        okText="确认添加"
        cancelText="取消"
      >
        <div className="native-clawpro-page__add-form">
          <label className="native-clawpro-page__add-field">
            <span>通道标识 (channel_id)</span>
            <Input
              placeholder="如 my-im-channel"
              value={addForm.channel_id}
              onChange={(e) =>
                setAddForm((f) => ({ ...f, channel_id: e.target.value }))
              }
            />
          </label>
          <label className="native-clawpro-page__add-field">
            <span>通道名称</span>
            <Input
              placeholder="如 企业内部 IM"
              value={addForm.name}
              onChange={(e) =>
                setAddForm((f) => ({ ...f, name: e.target.value }))
              }
            />
          </label>
          <label className="native-clawpro-page__add-field">
            <span>IM 服务器地址 (server.url)</span>
            <Input
              placeholder="如 https://im.example.com"
              value={addForm.server_url}
              onChange={(e) =>
                setAddForm((f) => ({ ...f, server_url: e.target.value }))
              }
            />
          </label>
          <div className="native-clawpro-page__add-field">
            <span>凭证字段 (cred_fields)</span>
            {addForm.cred_fields.map((field, idx) => (
              <div className="native-clawpro-page__cred-field-row" key={idx}>
                <Input
                  placeholder="key"
                  value={field.key}
                  style={{ width: '40%', marginRight: 8 }}
                  onChange={(e) => {
                    const next = [...addForm.cred_fields]
                    next[idx] = { ...next[idx], key: e.target.value }
                    setAddForm((f) => ({ ...f, cred_fields: next }))
                  }}
                />
                <Input
                  placeholder="label"
                  value={field.label}
                  style={{ width: '40%', marginRight: 8 }}
                  onChange={(e) => {
                    const next = [...addForm.cred_fields]
                    next[idx] = { ...next[idx], label: e.target.value }
                    setAddForm((f) => ({ ...f, cred_fields: next }))
                  }}
                />
                {addForm.cred_fields.length > 1 && (
                  <Button
                    icon={<MinusOutlined />}
                    size="small"
                    onClick={() => {
                      const next = addForm.cred_fields.filter(
                        (_, i) => i !== idx
                      )
                      setAddForm((f) => ({ ...f, cred_fields: next }))
                    }}
                  />
                )}
              </div>
            ))}
            <Button
              icon={<PlusOutlined />}
              size="small"
              style={{ marginTop: 8 }}
              onClick={() =>
                setAddForm((f) => ({
                  ...f,
                  cred_fields: [...f.cred_fields, { key: '', label: '' }]
                }))
              }
            >
              添加字段
            </Button>
          </div>
        </div>
      </Modal>

      {instanceState === 'created' && (
        <section className="native-clawpro-page__channels">
          <div className="native-clawpro-page__section-head">
            <span>手动配置通道</span>
            <h2>按通道参数逐项填写，保存后即可让数字员工接入对应 IM</h2>
          </div>
          <div className="native-clawpro-page__channels-shell">
            <div className="native-clawpro-page__channels-tip">
              <strong>当前支持手动配置</strong>
              <span>
                只展示云端明确返回了手动配置项的通道，避免出现无参数却无法配置的入口。
              </span>
            </div>
            {channelsLoading ? (
              <div className="native-clawpro-page__channels-loading">
                <Spin size="large" />
                <span>正在拉取通道配置项...</span>
              </div>
            ) : sortedChannels.length === 0 ? (
              <div className="native-clawpro-page__channels-loading">
                <span>
                  当前没有可手动配置的通道，请稍后再试或改走自动配置方案。
                </span>
              </div>
            ) : (
              <div className="native-clawpro-page__channels-grid">
                {sortedChannels.map((channel) => (
                  <div
                    className="native-clawpro-page__channel-card"
                    key={channel.channel_id}
                  >
                    <div className="native-clawpro-page__channel-head">
                      <div>
                        <strong>{channel.name}</strong>
                        <span>{channel.channel_id}</span>
                      </div>
                      {configuredStatusJSON &&
                        configuredStatusJSON.includes(channel.channel_id) && (
                          <em>已存在配置</em>
                        )}
                    </div>
                    <div className="native-clawpro-page__channel-fields">
                      {(channel.params || []).map((param) => (
                        <label
                          className="native-clawpro-page__channel-field"
                          key={`${channel.channel_id}-${param.key}`}
                        >
                          <span>{param.label}</span>
                          <Input
                            value={
                              channelFormValues[channel.channel_id]?.[
                                param.key
                              ] || ''
                            }
                            placeholder={`请输入${param.label}`}
                            onChange={(e) =>
                              handleChannelInputChange(
                                channel.channel_id,
                                param.key,
                                e.target.value
                              )
                            }
                          />
                        </label>
                      ))}
                    </div>
                    <div className="native-clawpro-page__channel-actions">
                      <Button
                        type="primary"
                        loading={savingChannelId === channel.channel_id}
                        onClick={() => handleSaveChannel(channel)}
                      >
                        保存{channel.name}配置
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  )
}

export default NativeClawProPage
